import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthorManager from "../../../services/home/AuthorManager";
import config from "../../../config";
import toast from "react-hot-toast";
import { motion } from "framer-motion";

const FeaturedAuthors = () => {
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  // currentIndex indicates the index of the first visible author in the window
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();
  const [visibleCount, setVisibleCount] = useState(6);

  useEffect(() => {
    const fetchAuthors = async () => {
      try {
        const data = await AuthorManager.getAllAuthors();
        if (data.responseCode === 200 && data.errorCode === null) {
          setAuthors(data.responseData);
        } else {
          toast.error(data.responseMessage || "Failed to load authors.");
        }
      } catch (error) {
        console.error("Failed to fetch authors:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAuthors();
  }, []);

  useEffect(() => {
    if (authors.length > visibleCount) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => {
          if (prevIndex >= authors.length - visibleCount) {
            return 0;
          }
          return prevIndex + 1;
        });
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [authors]);

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => {
      if (prevIndex === 0) {
        return authors.length - visibleCount;
      }
      return prevIndex - 1;
    });
  };

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => {
      if (prevIndex >= authors.length - visibleCount) {
        return 0;
      }
      return prevIndex + 1;
    });
  };

  const handleAuthorClick = (authorId) => {
    navigate(`/author/${authorId}`);
  };

  const onSeeAll = () => {
    // Navigate to JustLaunchedBooks page when "See All" is clicked
    navigate("/explore-authors");
  };

  useEffect(() => {
    const updateVisibleCount = () => {
      if (window.innerWidth < 640) {
        setVisibleCount(4); // was 3
      } else if (window.innerWidth < 768) {
        setVisibleCount(5); // was 4
      } else if (window.innerWidth < 1024) {
        setVisibleCount(7); // was 6 (unchanged)
      } else if (window.innerWidth < 1280) {
        setVisibleCount(8); // new for large screens
      } else {
        setVisibleCount(8); // new for xl+ screens
      }
    };

    updateVisibleCount();
    window.addEventListener("resize", updateVisibleCount);
    return () => window.removeEventListener("resize", updateVisibleCount);
  }, []);


  return (
    <section className="py-2 bg-white h-full">
      <div className="max-w-screen-xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h2 className="ml-2 md:ml-8 text-2xl md:text-3xl font-bold text-text-muted border-b-2 border-primary pb-1">
            Featured Authors
          </h2>

          <button
            onClick={onSeeAll}
            className="flex items-center gap-2 text-primary hover:text-primary-shade4 transition-colors group"
            aria-label="See all books"
          >
            <span className="font-medium">See All</span>

            {/* three arrows, staggered horizontal slide */}
            <div className="flex -space-x-2 overflow-hidden">
              {[0, 0.15].map((delay, i) => (
                <motion.svg
                  key={i}
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="w-5 h-5"
                  animate={{ x: [0, 6, 0] }}
                  transition={{
                    repeat: Infinity,
                    repeatType: "loop",
                    duration: 0.6,
                    delay,
                    ease: "easeInOut",
                  }}
                >
                  <path
                    d="M9 18L15 12L9 6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </motion.svg>
              ))}
            </div>
          </button>
        </div>

        {loading ? (
          <p className="text-center text-gray-600">Loading authors...</p>
        ) : authors.length === 0 ? (
          <p className="text-center text-gray-600">No authors found.</p>
        ) : (
          <div className="relative">
            {/* Left Arrow */}
            <button
              onClick={prevSlide}
              className="hidden md:block absolute top-1/2 transform -translate-y-1/2 p-2 bg-white bg-opacity-75 rounded-full shadow transition-all duration-300 ease-in-out hover:scale-110 left-2 md:-left-16"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="#FF7236"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            {/* Carousel Container */}
            <div className="overflow-x-auto md:overflow-x-hidden overflow-y-visible">
              <div
                className="flex transition-all duration-700 ease-in-out"
                style={{
                  transform: `translateX(-${
                    (currentIndex * 100) / visibleCount
                  }%)`,
                }}
              >
                {authors.map((author, idx) => (
                  <div
                    key={idx}
                    style={{ width: `${100 / visibleCount}%` }}
                    className="flex-none px-1 py-1 md:px-2 md:py-2 flex flex-col items-center"
                    onClick={() => handleAuthorClick(author.authorId)}
                  >
                    <img
                      src={
                        author.authorImage
                          ? `${config.url}/authorImages/${author.authorImage}`
                          : `/images/default_author_img.png`
                      }  onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/images/default_author_img.png";
                      }}
                      alt={author.authorName}
                      className="w-14 h-14 sm:w-20 sm:h-20 md:w-28 md:h-28 rounded-full object-cover shadow-md transition-transform duration-300 ease-in-out hover:scale-105 cursor-pointer"
                    />
                    <p className="mt-2 text-[10px] sm:text-xs font-medium text-gray-800 text-center leading-tight">
                      {author.authorName}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Arrow */}
            <button
              onClick={nextSlide}
              className="hidden md:block absolute top-1/2 transform -translate-y-1/2 p-2 bg-white bg-opacity-75 rounded-full shadow transition-all duration-300 ease-in-out hover:scale-110 right-2 md:-right-16"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="#FF7236"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedAuthors;
